import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, AlertTriangle, CheckCircle2, Trash2 } from "lucide-react";
import { ActionIcon } from "../components/ActionIcon";
import api, { formatApiError } from "../lib/api";
import { monthLabel } from "../lib/quarters";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

export default function AdminFinancials({ embedded = false }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [byEmployer, setByEmployer] = useState([]);
  const [clearTarget, setClearTarget] = useState(null);
  const [clearStep, setClearStep] = useState("warning");



  const loadByEmployer = async () => {
    try {
      const { data } = await api.get("/admin/feed/employers");
      setByEmployer(data);
    } catch { setByEmployer([]); }
  };

  const clearEmployer = async () => {
    if (!clearTarget) return;
    try {
      const { data } = await api.delete(`/admin/feed/employers/${encodeURIComponent(clearTarget.company)}`);
      toast.success(`Cleared ${data.cleared} row(s) for ${clearTarget.company}`);
      setClearTarget(null);
      setClearStep("warning");
      loadByEmployer();
      loadHistory();
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/feed/uploads");
      setHistory(data);
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
  };

  useEffect(() => {
    loadHistory();
    loadByEmployer();
  }, []);

  const runPreview = async (selected) => {
    setFile(selected);
    setPreview(null);
    if (!selected) return;
    setChecking(true);
    try {
      const fd = new FormData();
      fd.append("file", selected);
      const { data } = await api.post("/admin/feed/preview", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  const confirmUpload = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/feed/ingest", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Ingested ${data.rows} rows · replaced ${data.replaced} prior row(s)`);
      if (data.vendors_added) {
        toast.info(`${data.vendors_added} vendor record(s) added from the workbook payees`, { duration: 8000 });
      }
      if (data.kept_rates) {
        toast.info(`${data.kept_rates} manually maintained rate(s) were kept as set`, { duration: 8000 });
      }
      setFile(null);
      setPreview(null);
      loadHistory();
      loadByEmployer();
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const monthSpan = (months) =>
    months?.length ? `${monthLabel(months[0])} – ${monthLabel(months[months.length - 1])}` : "—";

  return (
    <div className={embedded ? "" : "mx-auto max-w-[1400px] px-6 lg:px-10 py-10"} data-testid="admin-financials-page">
      <div className="mb-8">
        {!embedded && (
          <>
            <div className="eyebrow mb-2">Administration</div>
            <h1 className="font-heading font-black text-5xl leading-none tracking-tight" style={{ color: "var(--ink)" }}>
              Monthly portal feed.
            </h1>
          </>
        )}
        <p className="mt-3 text-[15px] max-w-3xl" style={{ color: "var(--muted)" }}>
          Upload the portal workbook (.xlsx). It reads <span className="font-mono text-xs">EmployeesFirstCompanies,
          CaptiveFinancials, CaptivePerformance, VendorPayments, Rates, FWH-IRs</span> and the lookup sheets.
          Companies and lookups are refreshed; financial, performance and payment rows are replaced
          per company per month, so re-feeding a month is safe. Rates you have edited by hand are
          marked Manual and are never overwritten by an upload.
        </p>
        {history && (
          <p className="mt-3 text-sm" style={{ color: "var(--muted)" }} data-testid="financials-data-through">
            {history.company_count} companies · {history.row_count.toLocaleString()} financial rows ·{" "}
            {history.payment_count.toLocaleString()} vendor payments · data through {monthLabel(history.data_through)}
          </p>
        )}
      </div>

      <div className="border p-8" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <div className="flex flex-col md:flex-row md:items-end gap-6">
          <div className="flex-1">
            <label className="field-label">Portal workbook (.xlsx)</label>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => runPreview(e.target.files?.[0] || null)}
              data-testid="financials-file"
              className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-solid file:text-sm file:font-semibold file:bg-transparent"
            />
          </div>
          <button
            onClick={confirmUpload}
            disabled={!preview?.rows || saving}
            className="btn-primary flex items-center gap-2"
            data-testid="financials-confirm"
          >
            <UploadCloud size={15} /> {saving ? "Ingesting…" : "Confirm Ingest"}
          </button>
        </div>

        {checking && (
          <div className="mt-6 text-sm" style={{ color: "var(--muted)" }} data-testid="financials-checking">
            Reading the workbook…
          </div>
        )}

        {preview && (
          <div className="mt-8 border" style={{ borderColor: "var(--border)" }} data-testid="financials-preview">
            <div className="grid grid-cols-2 md:grid-cols-4 border-b" style={{ borderColor: "var(--border)" }}>
              <PreviewCell label="Rows parsed" value={preview.rows} testId="preview-parsed" />
              <PreviewCell label="Rows flagged" value={preview.issue_count} testId="preview-skipped" />
              <PreviewCell label="Companies" value={preview.companies.length} testId="preview-companies" />
              <PreviewCell label="Months covered" value={monthSpan(preview.months)} testId="preview-months" />
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-2 mb-6" data-testid="preview-sheets">
                {preview.sheets.map((sheet) => (
                  <div key={sheet.sheet} className="flex items-center justify-between text-xs border-b pb-1" style={{ borderColor: "var(--border)" }}>
                    <span className="font-mono" style={{ color: "var(--muted)" }}>{sheet.sheet}</span>
                    <span className="font-heading font-bold" style={{ color: "var(--ink)" }}>{sheet.rows}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {preview.companies.map((name) => (
                  <span key={name} className="pill pill-solid text-xs">{name}</span>
                ))}
              </div>
              {preview.issues.length === 0 ? (
                <div className="flex items-center gap-2 text-sm" style={{ color: "var(--ink)" }} data-testid="preview-clean">
                  <CheckCircle2 size={15} /> Every row parsed cleanly.
                </div>
              ) : (
                <div data-testid="preview-issues">
                  <div className="flex items-center gap-2 text-sm font-medium mb-3" style={{ color: "#B4472D" }}>
                    <AlertTriangle size={15} /> {preview.issue_count} row(s) will be skipped
                  </div>
                  <div className="border divide-y max-h-64 overflow-y-auto" style={{ borderColor: "var(--border)" }}>
                    {preview.issues.map((issue, index) => (
                      <div key={`${issue.sheet}-${issue.row}-${index}`} className="px-4 py-2 text-xs flex gap-4">
                        <span className="font-mono shrink-0" style={{ color: "var(--muted)" }}>
                          {issue.sheet} · row {issue.row}
                        </span>
                        <span style={{ color: "var(--ink)" }}>{issue.problem}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 border max-h-[460px] overflow-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="feed-by-employer">
        <div className="px-6 py-5 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="eyebrow">Data loaded by employer</div>
          <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--muted)" }}>
            Where each employer's financial data came from — the file, who loaded it and when. Clear an
            employer's rows if the data was loaded in error.
          </p>
        </div>
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-6 py-3">Employer</th>
              <th className="px-4 py-3">Months</th>
              <th className="px-4 py-3">Last load</th>
              <th className="px-4 py-3 text-right">Rows</th>
              <th className="px-4 py-3 text-right">Census</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {byEmployer.map((row) => (
              <tr key={row.company} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`feed-employer-${row.company.replace(/\s+/g, "-").toLowerCase()}`}>
                <td className="px-6 py-3 font-medium" style={{ color: "var(--ink)" }}>{row.company}</td>
                <td className="px-4 py-3 text-xs" style={{ color: "var(--muted)" }}>
                  {row.first_period ? `${monthLabel(row.first_period)} – ${monthLabel(row.last_period)}` : "—"}
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: "var(--muted)" }}>
                  {row.files.join(", ") || "—"}
                  <div>{row.fed_by.join(", ")}{row.last_fed_at ? ` · ${row.last_fed_at.slice(0, 10)}` : ""}</div>
                </td>
                <td className="px-4 py-3 text-right font-heading font-bold" style={{ color: "var(--ink)" }}>{row.rows}</td>
                <td className="px-4 py-3 text-right" style={{ color: "var(--muted)" }}>{row.census_employees || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <ActionIcon icon={Trash2} label="Clear this employer's loaded data" onClick={() => { setClearTarget(row); setClearStep("warning"); }} testId={`clear-feed-${row.company.replace(/\s+/g, "-").toLowerCase()}`}/>
                </td>
              </tr>
            ))}
            {!byEmployer.length && (
              <tr><td colSpan={6} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="feed-by-employer-empty">
                No financial data loaded yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-8 border max-h-[420px] overflow-y-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="financials-history">
        <div className="px-6 py-5 border-b eyebrow" style={{ borderColor: "var(--border)" }}>Ingest history</div>
        <div className="divide-y" style={{ borderColor: "var(--border)" }}>
          {(history?.uploads || []).map((row) => (
            <div key={row.id} className="grid grid-cols-12 px-6 py-4 gap-3 text-sm items-center" data-testid={`upload-row-${row.id}`}>
              <div className="col-span-12 md:col-span-4 font-medium truncate" style={{ color: "var(--ink)" }}>{row.filename || "—"}</div>
              <div className="col-span-6 md:col-span-2" style={{ color: "var(--muted)" }}>{row.uploaded_by}</div>
              <div className="col-span-6 md:col-span-2" style={{ color: "var(--muted)" }}>{row.uploaded_at.slice(0, 10)}</div>
              <div className="col-span-6 md:col-span-2 text-xs" style={{ color: "var(--muted)" }}>{monthSpan(row.months)}</div>
              <div className="col-span-6 md:col-span-2 text-right font-heading font-bold" style={{ color: "var(--ink)" }}>
                {row.rows} rows
              </div>
            </div>
          ))}
          {!history?.uploads?.length && (
            <div className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="history-empty">
              No feed ingests yet.
            </div>
          )}
        </div>
      </div>

      <Dialog open={Boolean(clearTarget)} onOpenChange={(open) => {
        if (!open) {
          setClearTarget(null);
          setClearStep("warning");
        }
      }}>
        <DialogContent className="!rounded-none max-w-lg" data-testid="feed-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tight">
              {clearStep === "warning" ? "Clear loaded financial data?" : "Confirm final deletion"}
            </DialogTitle>
            <DialogDescription>
              {clearTarget?.company}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm" style={{ color: "var(--muted)" }}>
            {clearStep === "warning"
              ? "This removes financial, performance, and vendor-payment feed rows for this employer."
              : "This destructive action cannot be undone from this page. Continue only if this feed was loaded in error."}
          </p>
          <DialogFooter>
            <button type="button" className="btn-ghost" onClick={() => { setClearTarget(null); setClearStep("warning"); }} data-testid="feed-delete-cancel">
              Cancel
            </button>
            {clearStep === "warning" ? (
              <button type="button" className="btn-primary" onClick={() => setClearStep("confirm")} data-testid="feed-delete-continue">
                Continue
              </button>
            ) : (
              <button type="button" className="btn-primary" onClick={clearEmployer} data-testid="feed-delete-confirm">
                Delete Loaded Data
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewCell({ label, value, testId }) {
  return (
    <div className="border-r last:border-r-0 p-5" style={{ borderColor: "var(--border)" }} data-testid={testId}>
      <div className="eyebrow text-[9px]">{label}</div>
      <div className="font-heading font-bold text-lg mt-1" style={{ color: "var(--ink)" }}>{value}</div>
    </div>
  );
}
